// ============================================================================
// Beacon — bulk deal finder. Paste a list of addresses, and one click enriches
// (fast path) + underwrites + scores each, returning a ranked shortlist. Turns
// a sourcing list (auctions, HUD, MLS) into decisions without copy-paste.
// ============================================================================

import type { Deal, Metrics } from "../types";
import { baseDeal } from "./deals";
import { calc } from "../engine/engine";
import { enrichDeal } from "./enrich";

export interface BulkResult {
  id: string;
  address: string;
  matched?: string;
  deal: Deal;
  metrics: Metrics;
  ok: boolean;
  note?: string;
}

/** Split a pasted blob into clean address lines (one per line, dedup blanks). */
export function parseAddresses(text: string): string[] {
  return text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
}

/** Minimal RFC-4180-ish CSV parser (handles quoted fields containing commas). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cur); rows.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim()));
}

/**
 * Turn CSV/TXT text into a list of addresses. Handles: a single full-address
 * column, separate Address/City/State/Zip columns, or a plain one-per-line list.
 */
export function parseCsvAddresses(text: string): string[] {
  const rows = parseCsv(text);
  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const hasHeader = header.some((h) => /address|street|city|state|zip|postal|property/.test(h));
  const body = hasHeader ? rows.slice(1) : rows;

  if (hasHeader) {
    const idx = (re: RegExp) => header.findIndex((h) => re.test(h));
    const iAddr = idx(/full ?address|property ?address|site ?address|street ?address|address ?1|^address$|^street$/);
    const iCity = idx(/city|town/);
    const iState = idx(/state|province|^st$/);
    const iZip = idx(/zip|postal/);
    return body
      .map((r) => {
        if (iAddr >= 0) {
          let a = (r[iAddr] || "").trim();
          if (iCity >= 0 && !a.includes(",")) a = [a, r[iCity], r[iState], r[iZip]].map((x) => (x || "").trim()).filter(Boolean).join(", ");
          return a;
        }
        return r.map((c) => c.trim()).filter(Boolean).join(", ");
      })
      .filter(Boolean);
  }
  // no header: each row is an address (reconstruct if it was comma-split)
  return body.map((r) => (r.length === 1 ? r[0].trim() : r.map((c) => c.trim()).filter(Boolean).join(", "))).filter(Boolean);
}

const CONCURRENCY = 4;

export async function analyzeAddresses(
  addresses: string[],
  onProgress?: (done: number, total: number) => void
): Promise<BulkResult[]> {
  const list = addresses.map((a) => a.trim()).filter(Boolean);
  const results: BulkResult[] = new Array(list.length);
  let done = 0;
  let idx = 0;

  async function worker() {
    while (idx < list.length) {
      const i = idx++;
      const addr = list[i];
      let deal = baseDeal({ addr, name: addr.split(",")[0].slice(0, 60) || "Prospect" });
      try {
        const enr = await enrichDeal(deal, { fast: true });
        deal = { ...deal, ...enr.patch };
        results[i] = { id: deal.id, address: addr, matched: enr.matchedAddress, deal, metrics: calc(deal), ok: true };
      } catch (e) {
        results[i] = { id: deal.id, address: addr, deal, metrics: calc(deal), ok: false, note: e instanceof Error ? e.message : String(e) };
      }
      done++;
      onProgress?.(done, list.length);
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, list.length) }, worker));

  // rank: gated deals sink to the bottom, otherwise by composite score
  const key = (r: BulkResult) => (r.metrics.score.gated ? -1 : r.metrics.score.composite);
  return results.filter(Boolean).sort((a, b) => key(b) - key(a));
}
